import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HowItWorks,
  ImportantNotice,
  LandingHero,
  useScrollToSetup,
} from './components/LandingSections';
import { SaveProgress } from './components/SaveProgress';
import { SetupForm, validateSetupForm, getCardIdFromQuery } from './components/SetupForm';
import type { SetupFormData } from './components/SetupForm';
import { SuccessScreen } from './components/SuccessScreen';
import { UrlPreview } from './components/UrlPreview';
import { isValidCardId } from './lib/card-id';
import { buildCardUrl, loadCard, saveCard } from './lib/storage';
import type { AppView, SavePhase } from './types/setup';

function emptyForm(cardId = ''): SetupFormData {
  return { cardId, network: '', coin: '', host: '', address: '' };
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function App() {
  const { setupRef, scrollToSetup } = useScrollToSetup();
  const queryCardId = getCardIdFromQuery();

  const [view, setView] = useState<AppView>('main');
  const [form, setForm] = useState<SetupFormData>(() => emptyForm(queryCardId));
  const [formError, setFormError] = useState<string | null>(null);
  const [savePhase, setSavePhase] = useState<SavePhase>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cardUrl, setCardUrl] = useState('');
  const [alreadyConfigured, setAlreadyConfigured] = useState(false);

  const cardIdLocked = isValidCardId(queryCardId);

  useEffect(() => {
    if (!isValidCardId(queryCardId)) return;

    loadCard(queryCardId).then((card) => {
      if (card?.configured && card?.address) {
        setAlreadyConfigured(true);
        setFormError('This card is already configured and cannot be set up again.');
        setCardUrl(buildCardUrl(queryCardId));
      }
    });
  }, [queryCardId]);

  const previewUrl = useMemo(() => {
    if (!isValidCardId(form.cardId.trim())) return '';
    return buildCardUrl(form.cardId.trim());
  }, [form.cardId]);

  const handleSave = useCallback(async () => {
    setFormError(null);
    setSaveError(null);

    const validation = validateSetupForm(form);
    if (!validation.ok) {
      setFormError(validation.msg);
      setupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (alreadyConfigured) {
      setFormError('This card is already configured.');
      return;
    }

    const cardId = form.cardId.trim();
    setSavePhase('validating');

    try {
      const existing = await loadCard(cardId);
      if (existing?.configured && existing?.address) {
        setSavePhase('idle');
        setFormError('This card is already configured and cannot be changed.');
        setAlreadyConfigured(true);
        return;
      }

      setSavePhase('saving');
      await sleep(300);

      await saveCard(cardId, {
        network: form.network,
        coin: form.coin,
        host: form.host,
        address: form.address,
      });

      setSavePhase('locking');
      await sleep(400);

      setSavePhase('completed');
      setCardUrl(buildCardUrl(cardId));
      await sleep(500);

      setSavePhase('idle');
      setView('success');
    } catch (err) {
      setSavePhase('idle');
      setSaveError(err instanceof Error ? err.message : 'Failed to save card configuration.');
    }
  }, [form, alreadyConfigured, setupRef]);

  const handleCancelSave = () => {
    setSavePhase('idle');
    setSaveError(null);
  };

  const handleFinish = () => {
    setView('main');
    setForm(emptyForm());
    setCardUrl('');
    setAlreadyConfigured(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (view === 'success') {
    return <SuccessScreen cardUrl={cardUrl} onFinish={handleFinish} />;
  }

  const isSaving = savePhase !== 'idle';

  return (
    <>
      <div className="wrap">
        <h1>Cronyl</h1>

        <LandingHero onStartSetup={scrollToSetup} />
        <HowItWorks />
        <ImportantNotice />

        <section className="setup-section" id="setup" ref={setupRef}>
          <h3 className="section-title">Card Setup</h3>

          <SetupForm
            values={form}
            onChange={setForm}
            error={formError}
            disabled={isSaving || alreadyConfigured}
            cardIdLocked={cardIdLocked}
          />

          <UrlPreview url={previewUrl} />

          <div className="setup-actions">
            <button
              type="button"
              className="btn btn-write"
              onClick={handleSave}
              disabled={isSaving || alreadyConfigured}
            >
              Complete Setup
            </button>
          </div>
        </section>
      </div>

      <SaveProgress phase={savePhase} error={saveError} onCancel={handleCancelSave} />
    </>
  );
}
